
import React, { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Smile } from 'lucide-react';
import EmojiPicker from './EmojiPicker';
import { cn } from '@/lib/utils';

interface InputWithEmojiPickerProps extends React.ComponentProps<typeof Input> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputWithEmojiPicker = React.forwardRef<HTMLInputElement, InputWithEmojiPickerProps>(
  ({ className, value, onChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleEmojiSelect = (emoji: string) => {
      const input = inputRef.current;
      if (!input) return;

      const cursorPosition = input.selectionStart || value.length;
      const newValue = value.slice(0, cursorPosition) + emoji + value.slice(cursorPosition);
      
      // Create a synthetic event to maintain compatibility with existing onChange handlers
      const syntheticEvent = {
        target: { ...input, value: newValue },
        currentTarget: { ...input, value: newValue }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);

      // Focus back to input and position cursor after the emoji
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(cursorPosition + emoji.length, cursorPosition + emoji.length);
      }, 0);
    };

    return (
      <div className="relative">
        <Input
          {...props}
          ref={inputRef}
          value={value}
          onChange={onChange}
          className={cn("pr-10", className)}
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <EmojiPicker onEmojiSelect={handleEmojiSelect}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-accent text-muted-foreground hover:text-foreground"
            >
              <Smile className="h-4 w-4" />
            </Button>
          </EmojiPicker>
        </div>
      </div>
    );
  }
);

InputWithEmojiPicker.displayName = "InputWithEmojiPicker";

export { InputWithEmojiPicker };
